import React, { useRef, useState, useEffect } from "react";
import { Eraser, Check, Loader2 } from "lucide-react";
import { uploadDataUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Canvas signature pad. Captures a drawn signature, uploads it as PNG,
// and reports the stored path via onSigned(url).
export function SignaturePad({ onSigned, value, testid }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#22d3ee";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  const confirm = async () => {
    if (!hasInk) { toast.error("Please sign first"); return; }
    setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const res = await uploadDataUrl(dataUrl);
      onSigned(res.url);
      toast.success("Signature captured");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save signature");
    } finally {
      setBusy(false);
    }
  };

  if (value) {
    return (
      <div className="rounded-xl border border-emerald-400/30 bg-[#0B0E1B] p-3 flex items-center gap-2 text-emerald-300 text-sm" data-testid={testid ? `${testid}-done` : undefined}>
        <Check className="w-4 h-4" /> Signature captured
      </div>
    );
  }

  return (
    <div>
      <div className="rounded-xl border border-[#2A3350] bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          width={520}
          height={160}
          data-testid={testid}
          className="w-full touch-none cursor-crosshair"
          style={{ height: 160 }}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={clear} className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg glass text-slate-300 hover:text-white">
          <Eraser className="w-3.5 h-3.5" /> Clear
        </button>
        <button type="button" onClick={confirm} disabled={busy} data-testid={testid ? `${testid}-confirm` : undefined}
          className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-lg btn-neon font-semibold disabled:opacity-60">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Confirm signature
        </button>
      </div>
    </div>
  );
}

export default SignaturePad;
